import React, { useState } from 'react';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface FormData {
  type: 'reparation' | 'montage' | '';
  // Réparation
  marqueVelo: string;
  typeVelo: string;
  descriptionPanne: string;
  photos: File[];
  // Montage
  typeVeloMontage: string;
  descriptionMontage: string;
  // Coordonnées
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  codePostal: string;
  ville: string;
}

const DevisAtelier: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    type: '',
    marqueVelo: '',
    typeVelo: '',
    descriptionPanne: '',
    photos: [],
    typeVeloMontage: '',
    descriptionMontage: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: ''
  });
  const [photosPreviews, setPhotosPreviews] = useState<string[]>([]);

  const marquesVelo = [
    'Specialized', 'Trek', 'Giant', 'Cannondale', 'Scott', 'Cervélo', 
    'Canyon', 'BMC', 'Pinarello', 'Colnago', 'Bianchi', 'Factor', 
    'Look', 'Lapierre', 'Time', 'Orbea', 'Wilier', 'De Rosa', 'Autre'
  ];

  const typesVelo = [
    { id: 'route', name: 'Vélo Route', icon: '/type velo loc/premium.png' },
    { id: 'gravel', name: 'Vélo Gravel', icon: '/type velo loc/gravel.png' },
    { id: 'triathlon', name: 'Vélo Triathlon', icon: '/type velo loc/triathlon.png' }
  ];

  const handleTypeSelect = (type: 'reparation' | 'montage') => {
    setFormData({ ...formData, type });
    setCurrentStep(2);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTypeVeloSelect = (typeVelo: string) => {
    if (formData.type === 'reparation') {
      setFormData({ ...formData, typeVelo });
    } else {
      setFormData({ ...formData, typeVeloMontage: typeVelo });
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newPhotos = Array.from(e.target.files);
      setFormData({ ...formData, photos: [...formData.photos, ...newPhotos] });
      
      // Create previews
      newPhotos.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotosPreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePhoto = (index: number) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
    setPhotosPreviews(photosPreviews.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    alert('Votre demande de devis a été envoyée ! Nous vous recontacterons dans les plus brefs délais.');
    console.log('Form data:', formData);
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!formData.type;
      case 2:
        if (formData.type === 'reparation') {
          return !!formData.marqueVelo && !!formData.typeVelo && !!formData.descriptionPanne;
        }
        return !!formData.typeVeloMontage && !!formData.descriptionMontage;
      case 3:
        return !!formData.nom && !!formData.prenom && !!formData.email && !!formData.telephone;
      default:
        return true;
    }
  };

  const renderStep1 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-center mb-8">Quel type de demande ?</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          onClick={() => handleTypeSelect('reparation')}
          className={`bg-white rounded-xl p-8 cursor-pointer transition-all duration-300 border-3 hover:shadow-xl ${
            formData.type === 'reparation' ? 'border-black shadow-xl' : 'border-gray-200 shadow-sm'
          }`}
        >
          <div className="text-center">
            <div className="text-5xl mb-4">🔧</div>
            <h3 className="font-bold text-xl mb-2">Réparation</h3>
            <p className="text-sm text-gray-600">
              Diagnostic, réparation de panne, remplacement de pièces usées ou cassées
            </p>
          </div>
        </div>

        <div
          onClick={() => handleTypeSelect('montage')}
          className={`bg-white rounded-xl p-8 cursor-pointer transition-all duration-300 border-3 hover:shadow-xl ${
            formData.type === 'montage' ? 'border-black shadow-xl' : 'border-gray-200 shadow-sm'
          }`}
        >
          <div className="text-center">
            <div className="text-5xl mb-4">🛠️</div>
            <h3 className="font-bold text-xl mb-2">Montage sur mesure</h3>
            <p className="text-sm text-gray-600">
              Assemblage complet d'un vélo à partir d'un cadre nu ou composants
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2Reparation = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-center mb-8">Détails de la réparation</h2>
      
      <div className="space-y-6">
        {/* Marque du vélo */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Marque du vélo <span className="text-red-500">*</span>
          </label>
          <select
            name="marqueVelo"
            value={formData.marqueVelo}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
          >
            <option value="">Sélectionnez une marque</option>
            {marquesVelo.map(marque => (
              <option key={marque} value={marque}>{marque}</option>
            ))}
          </select>
        </div>

        {/* Type de vélo */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Type de vélo <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-4">
            {typesVelo.map(type => (
              <div
                key={type.id}
                onClick={() => handleTypeVeloSelect(type.id)}
                className={`bg-white rounded-lg p-4 cursor-pointer transition-all border-2 ${
                  formData.typeVelo === type.id ? 'border-black shadow-lg' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="h-16 flex items-center justify-center mb-2">
                  <img src={type.icon} alt={type.name} className="h-full object-contain" />
                </div>
                <p className="text-center text-sm font-medium">{type.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Description de la panne */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Décrivez la panne, la casse ou le changement de pièces souhaité <span className="text-red-500">*</span>
          </label>
          <textarea
            name="descriptionPanne"
            value={formData.descriptionPanne}
            onChange={handleInputChange}
            rows={5}
            placeholder="Ex: Bruit au niveau du pédalier, dérailleur arrière qui saute, changement de cassette..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none"
          />
        </div>

        {/* Upload de photos */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Photos (optionnel)
          </label>
          <p className="text-sm text-gray-500 mb-3">Ajoutez des photos pour nous aider à mieux comprendre le problème</p>
          
          <div className="flex flex-wrap gap-4 mb-4">
            {photosPreviews.map((preview, index) => (
              <div key={index} className="relative w-24 h-24">
                <img src={preview} alt={`Aperçu ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                <button
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
            
            <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-xs text-gray-500 mt-1">Ajouter</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2Montage = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-center mb-8">Détails du montage</h2>
      
      <div className="space-y-6">
        {/* Type de vélo */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Type de vélo à monter <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-4">
            {typesVelo.map(type => (
              <div
                key={type.id}
                onClick={() => handleTypeVeloSelect(type.id)}
                className={`bg-white rounded-lg p-4 cursor-pointer transition-all border-2 ${
                  formData.typeVeloMontage === type.id ? 'border-black shadow-lg' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="h-16 flex items-center justify-center mb-2">
                  <img src={type.icon} alt={type.name} className="h-full object-contain" />
                </div>
                <p className="text-center text-sm font-medium">{type.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Description du montage */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Décrivez votre projet de montage <span className="text-red-500">*</span>
          </label>
          <textarea
            name="descriptionMontage"
            value={formData.descriptionMontage}
            onChange={handleInputChange}
            rows={6}
            placeholder="Ex: Montage complet à partir d'un cadre Specialized Tarmac SL7, groupe Shimano Dura-Ace Di2, roues Roval CLX 50..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none"
          />
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            <strong>💡 Conseil :</strong> N'hésitez pas à nous indiquer le cadre, le groupe, les roues et tous les composants souhaités. Si vous avez des questions sur la compatibilité ou des recommandations, nous vous conseillerons lors de notre échange.
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-center mb-8">Vos coordonnées</h2>
      
      <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Dupont"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              placeholder="Jean"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="jean.dupont@exemple.fr"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Téléphone <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <div className="flex items-center px-3 bg-gray-50 border-2 border-r-0 border-gray-200 rounded-l-lg">
                <span>🇫🇷</span>
                <span className="ml-2 text-sm text-gray-700">+33</span>
              </div>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                placeholder="6 12 34 56 78"
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-r-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Adresse
            </label>
            <input
              type="text"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              placeholder="12 rue de la Paix"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Code postal
            </label>
            <input
              type="text"
              name="codePostal"
              value={formData.codePostal}
              onChange={handleInputChange}
              placeholder="75001"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ville
            </label>
            <input
              type="text"
              name="ville"
              value={formData.ville}
              onChange={handleInputChange}
              placeholder="Paris"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const typeVeloName = typesVelo.find(t => t.id === (formData.type === 'reparation' ? formData.typeVelo : formData.typeVeloMontage))?.name || '';
    
    return (
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-8">Récapitulatif de votre demande</h2>
        
        <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 mb-1">Type de demande</p>
            <p className="font-bold text-lg">
              {formData.type === 'reparation' ? '🔧 Réparation' : '🛠️ Montage sur mesure'}
            </p>
          </div>

          {formData.type === 'reparation' ? (
            <>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Vélo</p>
                <p className="font-semibold">{formData.marqueVelo} - {typeVeloName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Description du problème</p>
                <p className="text-gray-700">{formData.descriptionPanne}</p>
              </div>
              {formData.photos.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Photos jointes</p>
                  <p className="text-gray-700">{formData.photos.length} photo(s)</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Type de vélo</p>
                <p className="font-semibold">{typeVeloName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Description du projet</p>
                <p className="text-gray-700">{formData.descriptionMontage}</p>
              </div>
            </>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">Vos coordonnées</p>
            <div className="space-y-1 text-sm">
              <p><span className="font-semibold">Nom :</span> {formData.prenom} {formData.nom}</p>
              <p><span className="font-semibold">Email :</span> {formData.email}</p>
              <p><span className="font-semibold">Téléphone :</span> +33 {formData.telephone}</p>
              {formData.adresse && (
                <p><span className="font-semibold">Adresse :</span> {formData.adresse}, {formData.codePostal} {formData.ville}</p>
              )}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>📞 Prochaine étape :</strong> Notre équipe vous contactera dans les 24-48h pour discuter de votre demande et vous fournir un devis personnalisé.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const steps = [
    { number: 1, title: "Type" },
    { number: 2, title: "Détails" },
    { number: 3, title: "Coordonnées" },
    { number: 4, title: "Confirmation" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 min-h-[80vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-black mb-4">
            Demande de devis Atelier
          </h1>
          <p className="text-center text-gray-600 mb-12">
            Réparation ou montage sur mesure
          </p>

          {/* Progress Steps */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        currentStep === step.number
                          ? 'bg-black text-white scale-110'
                          : currentStep > step.number
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step.number ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        step.number
                      )}
                    </div>
                    <p className={`text-xs mt-2 text-center font-medium ${
                      currentStep === step.number ? 'text-black' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-all ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div 
            className="relative rounded-2xl overflow-hidden min-h-[500px] mb-8"
            style={{
              backgroundImage: `url('/atelier velo/1152eebe1048aa89011d9a5f19fae6dc.jpg')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-white/90"></div>
            
            <div className="relative z-10 p-8 md:p-12">
              <div className="min-h-[400px] flex items-start justify-center pt-8">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && formData.type === 'reparation' && renderStep2Reparation()}
                {currentStep === 2 && formData.type === 'montage' && renderStep2Montage()}
                {currentStep === 3 && renderStep3()}
                {currentStep === 4 && renderStep4()}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 max-w-2xl mx-auto">
                {currentStep > 1 ? (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                  </button>
                ) : <div></div>}
                
                {currentStep < 4 ? (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${
                      canProceed()
                        ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Continuer
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 bg-green-600 text-white px-10 py-4 rounded-lg font-bold hover:bg-green-700 transition-colors shadow-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Envoyer ma demande
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default DevisAtelier;
